import React from "react";
import { motion } from "framer-motion";

// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import { alpha, useTheme } from "@mui/material/styles";

// ICONS
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import PaidIcon from "@mui/icons-material/Paid";
import { formatFinance } from "../../utils/formatters";

// Animation variants
const cardVariants = {
  hover: {
    y: -4,
    boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 20,
    },
  },
  tap: {
    scale: 0.98,
    transition: { duration: 0.1 },
  },
};

const iconVariants = {
  hover: {
    rotate: [0, -10, 10, -10, 0],
    transition: {
      duration: 0.5,
    },
  },
};

function CardFinance({
  cardSize = "medium",
  label,
  value = 0,
  subValue = 0,
  onlineValue,
  moneyValue,
  cardValue,
  headerColor,
  textColor,
  isClickable = false,
  onClick = null,
}) {
  const theme = useTheme();

  // Default colors
  const defaultHeaderColor = headerColor || theme.palette.text.primary;
  const defaultTextColor = textColor || theme.palette.text.primary;
  const defaultValueColor = textColor || theme.palette.success.main;

  // Conditional content
  const renderPaymentMethod = (label, val, icon) => {
    if (val !== undefined && val !== null) {
      return (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            mt: 1,
            p: 1,
            borderRadius: 1,
            backgroundColor: alpha(theme.palette.background.paper, 0.5),
          }}
        >
          {icon}
          <Typography
            variant="body2"
            fontWeight="medium"
            color={defaultTextColor}
          >
            {label}:
          </Typography>
          <Typography
            variant="body2"
            fontWeight="bold"
            color={defaultValueColor}
            sx={{ ml: "auto" }}
          >
            {formatFinance(val)}
          </Typography>
        </Stack>
      );
    }
    return null;
  };

  const MotionComponent = isClickable ? motion.div : React.Fragment;
  const motionProps = isClickable
    ? {
        whileHover: "hover",
        whileTap: "tap",
        variants: cardVariants,
        onClick,
        style: { cursor: "pointer" },
      }
    : {};

  return (
    <MotionComponent {...motionProps}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          backgroundColor: theme.palette.background.paper,
          overflow: "hidden",
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          height: "100%",
          transition: "all 0.2s ease-in-out",
          "&:hover": isClickable
            ? {
                boxShadow: theme.shadows[2],
                borderColor: alpha(theme.palette.primary.main, 0.2),
              }
            : {},
        }}
      >
        {cardSize !== "small" && label && (
          <>
            <Box
              sx={{
                p: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <motion.div variants={iconVariants} whileHover="hover">
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      borderRadius: 1.5,
                      p: 0.75,
                      width: 32,
                      height: 32,
                    }}
                  >
                    <LocalAtmIcon fontSize="small" />
                  </Box>
                </motion.div>
                <Typography
                  variant="body1"
                  fontWeight="600"
                  color={defaultHeaderColor}
                >
                  {label}
                </Typography>
              </Stack>
            </Box>
          </>
        )}

        <Box sx={{ p: 2 }}>
          <Typography
            variant={cardSize === "small" ? "h5" : "h4"}
            align="center"
            fontWeight="bold"
            sx={{
              color: defaultValueColor,
              my: 1.5,
            }}
          >
            {formatFinance(value)}
          </Typography>

          {subValue > 0 && (
            <>
              <Divider sx={{ my: 1.5, opacity: 0.6 }} />
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  p: 1,
                  borderRadius: 1,
                  backgroundColor: alpha(theme.palette.error.main, 0.05),
                }}
              >
                <MoneyOffIcon
                  fontSize="small"
                  sx={{ color: theme.palette.error.main }}
                />
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  color={theme.palette.error.main}
                >
                  Canceladas:
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color={theme.palette.error.main}
                  sx={{ ml: "auto" }}
                >
                  {formatFinance(subValue)}
                </Typography>
              </Stack>
            </>
          )}

          {(onlineValue !== undefined ||
            moneyValue !== undefined ||
            cardValue !== undefined) && (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight="medium"
                sx={{ display: "block", mb: 1 }}
              >
                DETALHAMENTO
              </Typography>

              {renderPaymentMethod(
                "Online",
                onlineValue,
                <PhoneAndroidIcon
                  fontSize="small"
                  sx={{ color: theme.palette.info.main }}
                />
              )}

              {renderPaymentMethod(
                "Dinheiro",
                moneyValue,
                <PaidIcon
                  fontSize="small"
                  sx={{ color: theme.palette.success.main }}
                />
              )}

              {renderPaymentMethod(
                "Cartão",
                cardValue,
                <CreditCardIcon
                  fontSize="small"
                  sx={{ color: theme.palette.warning.main }}
                />
              )}
            </Box>
          )}
        </Box>
      </Paper>
    </MotionComponent>
  );
}

export default CardFinance;
